import React, { useEffect, lazy, Suspense } from "react";
import { registerSW } from 'virtual:pwa-register';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Layout } from "./components/layout/Layout";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import "./App.css";

import HomePage from "./pages/home";
const TreinosPage = lazy(() => import("./pages/treinos"));
const DuduPage = lazy(() => import("./pages/dudu-daneluci"));
const GabiPage = lazy(() => import("./pages/gabi-vergilio"));

const AplicativoPage = lazy(() => import("./pages/aplicativo-de-treino"));
const BioPage = lazy(() => import("./pages/bio"));

const ContatoPage = lazy(() => import("./pages/contato"));
const EquipePage = lazy(() => import("./pages/equipe"));
const CentroPage = lazy(() => import("./pages/centro-de-treinamento"));
const TrainingCampsPage = lazy(() => import("./pages/training-camps"));
const CatalogoPage = lazy(() => import("./pages/catalogo-de-produtos"));
const AssessoriaCiclismoPage = lazy(() => import("./pages/assessoria-ciclismo"));
const PoliticaPrivacidadePage = lazy(() => import("./pages/politica-de-privacidade"));
const CampDetailsPage = lazy(() => import("./pages/training-camps/CampDetails"));
const NotFoundPage = lazy(() => import("./pages/NotFound"));

const Loading = () => (
  <div className="flex h-svh w-full items-center justify-center bg-white">
    <div className="size-12 animate-pulse rounded-full bg-brand-cyan/10" />
  </div>
);

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  state = { hasError: false, error: undefined as Error | undefined };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Erro ao renderizar página:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "2rem", textAlign: "center", fontFamily: "sans-serif", backgroundColor: "#000", color: "#fff", minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <h1 style={{ color: "#4DDBE2" }}>Ops! Algo deu errado.</h1>
          <p style={{ opacity: 0.7 }}>{this.state.error?.message}</p>
          <button 
            onClick={() => window.location.href = "/"}
            style={{ 
              marginTop: "1.5rem", 
              padding: "0.75rem 1.5rem", 
              backgroundColor: "#4DDBE2", 
              border: "none", 
              borderRadius: "12px", 
              cursor: "pointer",
              fontWeight: "bold"
            }}
          >
            Recarregar aplicação
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <Routes location={location}>
      {/* Rota standalone — sem Header/Footer */}
      <Route path="/bio" element={<BioPage />} />

      {/* Rotas com Layout padrão */}
      <Route path="/" element={<HomePage />} />
      <Route path="/aplicativo-de-treino" element={<AplicativoPage />} />
      <Route path="/contato" element={<ContatoPage />} />
      <Route path="/treinos" element={<TreinosPage />} />
      <Route path="/equipe" element={<EquipePage />} />
      <Route path="/centro-de-treinamento" element={<CentroPage />} />
      <Route path="/training-camps" element={<TrainingCampsPage />} />
      <Route path="/training-camps/:slug" element={<CampDetailsPage />} />
      <Route path="/dudu-daneluci" element={<DuduPage />} />
      <Route path="/assessoria-ciclismo" element={<AssessoriaCiclismoPage />} />
      <Route path="/gabi-vergilio" element={<GabiPage />} />
      <Route path="/catalogo-de-produtos" element={<CatalogoPage />} />
      {/* Redireciona URL com acento para versão sem acento */}
      <Route path="/gabi-vergílio" element={<Navigate to="/gabi-vergilio" replace />} />
      <Route path="/catálogo-de-produtos" element={<Navigate to="/catalogo-de-produtos" replace />} />
      <Route path="/politica-de-privacidade" element={<PoliticaPrivacidadePage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

import { LazyMotion, domAnimation } from "framer-motion";

/**
 * Decide se envolve as rotas com Layout (Header/Footer) ou não.
 * A página /bio é standalone — não usa Navbar nem Footer.
 */
function AppContent() {
  const location = useLocation();
  const isBioPage = location.pathname === "/bio";

  if (isBioPage) {
    return <AppRoutes />;
  }

  return (
    <Layout>
      <AppRoutes />
    </Layout>
  );
}

function App() {
  useEffect(() => {
    // Registra o Service Worker após 5s para não bloquear o render inicial
    const timeout = setTimeout(() => {
      registerSW({
        onNeedRefresh() {
          console.log('Novo conteúdo disponível. Recarregue para atualizar.');
        },
        onOfflineReady() {
          console.log('App pronto para funcionar offline.');
        },
      });
    }, 5000);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Suspense fallback={<Loading />}>
            <LazyMotion features={domAnimation} strict>
              <AppContent />
            </LazyMotion>
          </Suspense>
        </BrowserRouter>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
